/**
 * <product-columns-gallery>
 *
 * Two-way binding between the media gallery and the variant pickers.
 *
 *   gallery -> variant : clicking a thumbnail, or using prev/next, selects the
 *                        variant that owns the newly shown media.
 *   variant -> gallery : changing a dropdown shows that variant's media and
 *                        highlights its thumbnail.
 *
 * The variant -> gallery direction listens on the product form's hidden
 * `input[name="id"]`, which theme's VariantSelects updates (and fires `change`
 * on) only after it has resolved the new variant. That is the one signal that
 * is guaranteed to be correct and late enough.
 *
 * The gallery -> variant direction is only ever triggered by real user input,
 * never by programmatic media changes, so the two directions cannot loop.
 */
class ProductColumnsGallery extends HTMLElement {
	connectedCallback() {
		this.sectionId = this.dataset.section;

		this.frames = Array.from(this.querySelectorAll('[data-media-id][data-frame]'));
		this.thumbs = Array.from(this.querySelectorAll('[data-media-id][data-thumb]'));
		this.prevButton = this.querySelector('[data-gallery-prev]');
		this.nextButton = this.querySelector('[data-gallery-next]');
		this.counterCurrent = this.querySelector('[data-gallery-current]');
		this.zoomButton = this.querySelector('[data-gallery-zoom]');
		this.lightbox = this.querySelector('[data-gallery-lightbox]');
		this.lightboxImage = this.lightbox ? this.lightbox.querySelector('[data-lightbox-img]') : null;
		this.lightboxCurrent = this.lightbox ? this.lightbox.querySelector('[data-lightbox-current]') : null;

		this.variantMap = this.readVariantMap();
		this.mediaOrder = this.frames.map((frame) => frame.dataset.mediaId);

		this.activeMediaId = this.mediaOrder[0] || null;
		const initiallyActive = this.querySelector('[data-frame][data-active]');
		if (initiallyActive) this.activeMediaId = initiallyActive.dataset.mediaId;

		this.bindEvents();
		this.render();
	}

	/* ---------------------------------------------------------------- data */

	readVariantMap() {
		const node = this.querySelector('[data-variant-media-map]');
		if (!node) return [];

		try {
			return JSON.parse(node.textContent) || [];
		} catch (error) {
			console.warn('[product-columns-gallery] could not parse variant media map', error);
			return [];
		}
	}

	get variantSelects() {
		return document.querySelector(
			`variant-selects[data-section="${this.sectionId}"], variant-radios[data-section="${this.sectionId}"]`
		);
	}

	get variantIdInput() {
		return document.querySelector(`#product-form-${this.sectionId} input[name="id"]`);
	}

	/** Option values currently chosen in the pickers, in option order. */
	currentOptions() {
		const root = this.variantSelects;
		if (!root) return [];

		const selects = root.querySelectorAll('select');
		if (selects.length) return Array.from(selects, (select) => select.value);

		return Array.from(root.querySelectorAll('fieldset'), (fieldset) => {
			const checked = fieldset.querySelector('input:checked');
			return checked ? checked.value : null;
		});
	}

	/* -------------------------------------------------------------- events */

	bindEvents() {
		this.thumbs.forEach((thumb) => {
			thumb.addEventListener('click', (event) => {
				event.preventDefault();
				this.handleUserMediaChange(thumb.dataset.mediaId);
			});
		});

		if (this.prevButton) {
			this.prevButton.addEventListener('click', () => this.step(-1));
		}

		if (this.nextButton) {
			this.nextButton.addEventListener('click', () => this.step(1));
		}

		this.bindLightbox();

		// variant -> gallery.
		// Delegated on document, and resolved from the pickers themselves
		// rather than the product form's hidden input: this template has no
		// buy box, so that input does not exist. Reading the option values
		// keeps this direction working with or without a form on the page.
		document.addEventListener('change', (event) => {
			const root = this.variantSelects;
			if (!root || !event.target.closest) return;
			if (!root.contains(event.target)) return;

			this.showMediaForSelectedOptions();
			this.pruneUnavailableOptions();
		});

		this.pruneUnavailableOptions();
	}

	/* --------------------------------------------------- option availability */

	/**
	 * The theme blanks the label of an unreachable option instead of removing
	 * it (it substitutes variantStrings.unavailable_with_option, which is an
	 * empty string in locales/nl.json), leaving rows of blank entries in the
	 * dropdowns.
	 *
	 * Hide anything no real variant can reach, working left to right: an
	 * option is offered only if some variant matches every option chosen
	 * before it. If the current choice gets hidden, fall to the first option
	 * that is still selectable and re-run, so the pickers always sit on a
	 * combination that exists.
	 */
	pruneUnavailableOptions(depth = 0) {
		const root = this.variantSelects;
		if (!root || depth > 4) return;

		const selects = Array.from(root.querySelectorAll('select'));
		if (!selects.length) return;

		let corrected = false;

		selects.forEach((select, index) => {
			const prefix = selects.slice(0, index).map((s) => s.value);

			const reachable = new Set(
				this.variantMap
					.filter((variant) =>
						prefix.every((value, i) => variant.options[i] === value)
					)
					.map((variant) => variant.options[index])
			);

			Array.from(select.options).forEach((option) => {
				const ok = reachable.has(option.value);
				option.hidden = !ok;
				option.disabled = !ok;
				// Undo the theme's blanking so surviving labels read correctly.
				if (ok && !option.textContent.trim()) option.textContent = option.value;
			});

			if (!reachable.has(select.value)) {
				const fallback = Array.from(select.options).find((o) => !o.hidden);
				if (fallback && select.value !== fallback.value) {
					select.value = fallback.value;
					corrected = true;
				}
			}
		});

		if (corrected) {
			root.dispatchEvent(new Event('change', { bubbles: true }));
			this.pruneUnavailableOptions(depth + 1);
		}
	}

	/* ---------------------------------------------------------- full screen */

	bindLightbox() {
		if (!this.zoomButton) return;
		if (!this.lightbox || typeof this.lightbox.showModal !== 'function') {
			this.zoomButton.hidden = true;
			return;
		}

		const on = (selector, handler) => {
			const element = this.lightbox.querySelector(selector);
			if (element) element.addEventListener('click', handler);
		};

		this.zoomButton.addEventListener('click', () => this.openLightbox());
		on('[data-lightbox-close]', () => this.closeLightbox());
		// prev/next in the full-screen view are the gallery's own: they select the variant too.
		on('[data-lightbox-prev]', () => this.step(-1));
		on('[data-lightbox-next]', () => this.step(1));

		// a click beside the image (backdrop or empty stage) closes
		this.lightbox.addEventListener('click', (event) => {
			if (event.target === this.lightbox || event.target.classList.contains('m-lightbox__stage')) {
				this.closeLightbox();
			}
		});
		this.lightbox.addEventListener('keydown', (event) => {
			if (event.key === 'ArrowLeft') this.step(-1);
			if (event.key === 'ArrowRight') this.step(1);
			// Esc explicitly: any other handler cancelling this keydown would
			// otherwise swallow the dialog's native close request.
			if (event.key === 'Escape') {
				event.preventDefault();
				this.closeLightbox();
			}
		});
		this.lightbox.addEventListener('close', () => {
			document.documentElement.classList.remove('m-lightbox-open');
			this.zoomButton.focus();
		});
	}

	closeLightbox() {
		document.documentElement.classList.remove('m-lightbox-open');
		if (this.lightbox.open) this.lightbox.close();
	}

	openLightbox() {
		this.syncLightbox(true);
		if (!this.lightboxImage || this.lightboxImage.hidden) return; // active media is not an image
		document.documentElement.classList.add('m-lightbox-open');
		this.lightbox.showModal();
	}

	/**
	 * Mirror the active frame into the full-screen view. The 2048px source is
	 * only assigned while the view is open (or opening), so browsing the
	 * gallery never downloads the large files.
	 */
	syncLightbox(force = false) {
		const frame = this.frames.find((item) => item.dataset.mediaId === this.activeMediaId);
		const full = frame ? frame.dataset.full || '' : '';

		if (this.zoomButton) this.zoomButton.hidden = !full;
		if (!this.lightbox || !(force || this.lightbox.open)) return;

		if (this.lightboxImage) {
			if (full) {
				if (this.lightboxImage.getAttribute('src') !== full) this.lightboxImage.setAttribute('src', full);
				const source = frame.querySelector('img');
				this.lightboxImage.alt = source ? source.alt : '';
			} else {
				this.lightboxImage.removeAttribute('src');
			}
			this.lightboxImage.hidden = !full;
		}
		if (this.lightboxCurrent) {
			this.lightboxCurrent.textContent = String(this.mediaOrder.indexOf(this.activeMediaId) + 1).padStart(2, '0');
		}
	}

	step(direction) {
		if (!this.mediaOrder.length) return;

		const index = this.mediaOrder.indexOf(this.activeMediaId);
		const nextIndex = (index + direction + this.mediaOrder.length) % this.mediaOrder.length;
		this.handleUserMediaChange(this.mediaOrder[nextIndex]);
	}

	/**
	 * A media change the shopper caused: move the gallery *and* pull the
	 * variant pickers across to match.
	 */
	handleUserMediaChange(mediaId) {
		this.setActiveMedia(mediaId);
		this.selectVariantForMedia(mediaId);
	}

	/* ------------------------------------------------------ gallery -> variant */

	/**
	 * Several variants can share one image (e.g. every size of the same
	 * pictogram). Prefer the candidate that keeps the shopper's existing
	 * choices — changing the picture should not silently reset their size.
	 */
	pickVariantForMedia(mediaId) {
		const candidates = this.variantMap.filter((variant) => String(variant.mediaId) === String(mediaId));
		if (!candidates.length) return null;
		if (candidates.length === 1) return candidates[0];

		const current = this.currentOptions();
		let best = null;
		let bestScore = -Infinity;

		candidates.forEach((candidate) => {
			let score = 0;
			(candidate.options || []).forEach((value, index) => {
				if (value === current[index]) score += 1;
			});
			// Break ties towards something the shopper can actually buy.
			if (candidate.available) score += 0.5;

			if (score > bestScore) {
				bestScore = score;
				best = candidate;
			}
		});

		return best;
	}

	selectVariantForMedia(mediaId) {
		const variant = this.pickVariantForMedia(mediaId);
		if (!variant) return; // media not tied to any variant — browse only

		const root = this.variantSelects;
		if (!root) return;

		let changed = false;

		const selects = root.querySelectorAll('select');
		if (selects.length) {
			selects.forEach((select, index) => {
				const value = variant.options[index];
				if (value != null && select.value !== value) {
					select.value = value;
					changed = true;
				}
			});
		} else {
			root.querySelectorAll('fieldset').forEach((fieldset, index) => {
				const value = variant.options[index];
				const input = fieldset.querySelector(`input[value="${CSS.escape(String(value))}"]`);
				if (input && !input.checked) {
					input.checked = true;
					changed = true;
				}
			});
		}

		if (!changed) return;

		// Hand off to the theme's own variant machinery (price, SKU, URL,
		// availability, add-to-cart state) rather than reimplementing it.
		root.dispatchEvent(new Event('change', { bubbles: true }));
	}

	/* ------------------------------------------------------ variant -> gallery */

	showMediaForVariant(variantId) {
		const variant = this.variantMap.find((entry) => String(entry.id) === String(variantId));
		if (!variant || variant.mediaId == null) return;

		this.setActiveMedia(String(variant.mediaId));
	}

	/** Match the chosen option values against the variant map. */
	showMediaForSelectedOptions() {
		const current = this.currentOptions();
		if (!current.length) return;

		const variant = this.variantMap.find((entry) =>
			(entry.options || []).every((value, index) => value === current[index])
		);

		if (!variant || variant.mediaId == null) return;

		this.setActiveMedia(String(variant.mediaId));
	}

	/* -------------------------------------------------------------- render */

	setActiveMedia(mediaId) {
		if (!mediaId || mediaId === this.activeMediaId) return;
		if (!this.mediaOrder.includes(String(mediaId))) return;

		this.activeMediaId = String(mediaId);
		this.render();
	}

	render() {
		this.frames.forEach((frame) => {
			if (frame.dataset.mediaId === this.activeMediaId) {
				frame.setAttribute('data-active', '');
			} else {
				frame.removeAttribute('data-active');
			}
		});

		let activeThumb = null;
		this.thumbs.forEach((thumb) => {
			const isActive = thumb.dataset.mediaId === this.activeMediaId;
			thumb.setAttribute('aria-current', isActive ? 'true' : 'false');
			if (isActive) activeThumb = thumb;
		});

		if (activeThumb) this.scrollThumbIntoView(activeThumb);

		if (this.counterCurrent) {
			this.counterCurrent.textContent = String(this.mediaOrder.indexOf(this.activeMediaId) + 1).padStart(2, '0');
		}

		this.syncLightbox();
	}

	/** Keep the highlighted thumbnail visible without scrolling the page. */
	scrollThumbIntoView(thumb) {
		const container = thumb.parentElement;
		if (!container || container.scrollHeight <= container.clientHeight) return;

		const top = thumb.offsetTop - container.offsetTop;
		const bottom = top + thumb.offsetHeight;

		if (top < container.scrollTop) {
			container.scrollTop = top;
		} else if (bottom > container.scrollTop + container.clientHeight) {
			container.scrollTop = bottom - container.clientHeight;
		}
	}
}

if (!customElements.get('product-columns-gallery')) {
	customElements.define('product-columns-gallery', ProductColumnsGallery);
}
